import React, { useEffect, useRef, useState } from 'react';
import type { StoryVariableDef } from '../../../services/storiesAdminService';

/**
 * Обёртка над <input> / <textarea> с popover-автокомплитом переменных.
 *
 * Триггер: после `{{` в позиции курсора показывается список доступных
 * переменных. Фильтрация — по подстроке после `{{`. Клик / Enter
 * вставляет `{{path}}` и сдвигает курсор за вставленный токен.
 *
 * variables — список path'ов, доступных в этом контексте (story.data_sources
 * + iterate-over item.X если slide.iterate_over задан). Пустой массив →
 * autocomplete отключён (поле работает как обычный input).
 */
interface Props {
  value: string;
  onChange: (next: string) => void;
  variables: StoryVariableDef[];
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
}

const VariableAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  variables,
  multiline,
  rows,
  placeholder,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [triggerStart, setTriggerStart] = useState<number | null>(null);
  const [selected, setSelected] = useState(0);

  const filtered = variables.filter((v) =>
    v.path.toLowerCase().includes(query.toLowerCase()) ||
    v.label.toLowerCase().includes(query.toLowerCase()),
  );

  /** Анализирует текст до курсора. Если последние 2 символа `{{` ИЛИ внутри
   * незакрытого `{{...` — открывает popover и считает query. */
  const detectTrigger = (text: string, cursor: number) => {
    if (variables.length === 0) {
      setOpen(false);
      return;
    }
    // Ищем последний `{{` до курсора, без `}}` между ним и курсором.
    const beforeCursor = text.slice(0, cursor);
    const lastOpen = beforeCursor.lastIndexOf('{{');
    if (lastOpen === -1) {
      setOpen(false);
      return;
    }
    const afterOpen = beforeCursor.slice(lastOpen + 2);
    if (afterOpen.includes('}}')) {
      setOpen(false);
      return;
    }
    // Слишком длинная query (>40) — скорее всего пользователь забыл закрыть.
    if (afterOpen.length > 40) {
      setOpen(false);
      return;
    }
    setTriggerStart(lastOpen);
    setQuery(afterOpen);
    setSelected(0);
    setOpen(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = e.target.value;
    onChange(next);
    detectTrigger(next, e.target.selectionStart ?? next.length);
  };

  const handleSelect = (path: string) => {
    if (triggerStart === null || !inputRef.current) return;
    const el = inputRef.current;
    const before = value.slice(0, triggerStart);
    const after = value.slice((el.selectionStart ?? value.length));
    const insertion = `{{${path}}}`;
    const next = before + insertion + after;
    onChange(next);
    setOpen(false);
    // Move cursor after inserted token. Defer until React rerenders.
    setTimeout(() => {
      if (!inputRef.current) return;
      const pos = before.length + insertion.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => (s + 1) % filtered.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => (s - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleSelect(filtered[selected].path);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  // Закрытие popover при клике вне.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)
          && inputRef.current !== e.target) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const baseProps = {
    ref: inputRef as any,
    value,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    placeholder,
    className: className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
  };

  return (
    <div className="relative">
      {multiline ? (
        <textarea {...(baseProps as any)} rows={rows || 3} />
      ) : (
        <input type="text" {...(baseProps as any)} />
      )}
      {open && filtered.length > 0 && (
        <div
          ref={popoverRef}
          className="absolute z-20 mt-1 max-h-72 w-full overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{ top: '100%', left: 0 }}
        >
          <div className="px-3 py-1.5 text-xs text-gray-500 bg-gray-50 border-b sticky top-0">
            {filtered.length} переменных — ↑↓ + Enter / Tab
          </div>
          {filtered.slice(0, 12).map((v, idx) => (
            <button
              key={v.path}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(v.path); }}
              onMouseEnter={() => setSelected(idx)}
              className={`w-full text-left px-3 py-2 text-sm flex items-baseline gap-3 ${
                idx === selected ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <code className="font-mono text-blue-600 whitespace-nowrap">{`{{${v.path}}}`}</code>
              <span className="text-gray-700 truncate flex-1">{v.label}</span>
              <span className="text-xs text-gray-400 whitespace-nowrap">{v.example}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default VariableAutocomplete;
